import { SizableText, XStack } from 'tamagui'

export interface RadioChipsOption<T extends string> {
    value: T
    label: string
}

/**
 * Segmented "radio chips" control (BuyerFlo design system): a joined button group
 * on a raised track; the selected chip gets a flat accent fill. Sizes to its
 * content by default; pass `fullWidth` to stretch the chips evenly.
 */
export function RadioChips<T extends string>({
    options,
    value,
    onChange,
    fullWidth,
    disabled,
}: {
    options: RadioChipsOption<T>[]
    value: T | undefined
    onChange: (value: T) => void
    fullWidth?: boolean
    disabled?: boolean
}) {
    return (
        <XStack
            alignSelf={fullWidth ? 'stretch' : 'flex-start'}
            backgroundColor="$backgroundRaised"
            borderRadius={8}
            padding={3}
            gap={3}
            opacity={disabled ? 0.5 : 1}
        >
            {options.map(opt => {
                const active = opt.value === value
                return (
                    <XStack
                        key={opt.value}
                        flex={fullWidth ? 1 : undefined}
                        justifyContent="center"
                        alignItems="center"
                        height={28}
                        paddingHorizontal="$3"
                        borderRadius={6}
                        cursor={disabled ? 'default' : 'pointer'}
                        animation="quick"
                        backgroundColor={active ? '$primary' : 'transparent'}
                        hoverStyle={{ backgroundColor: active ? '$primary' : '$backgroundHover' }}
                        pressStyle={{ opacity: 0.85 }}
                        onPress={() => !disabled && onChange(opt.value)}
                    >
                        <SizableText
                            fontSize={13}
                            fontWeight={active ? '600' : '500'}
                            color={active ? 'white' : '$colorSoft'}
                            numberOfLines={1}
                        >
                            {opt.label}
                        </SizableText>
                    </XStack>
                )
            })}
        </XStack>
    )
}
